import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Divider
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    NavigateBefore as NavigateBeforeIcon,
    NavigateNext as NavigateNextIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { getWeekTimecards } from '../../../services/timecardService';
import { getAllUsersNoPagination } from '../../../services/userService';
import { useAuth } from '../../../context/AuthContext';
import ExportButton from '../../../components/common/ExportButton';
import { getWeekInfo } from '../../../utils/dateUtils';

// Utility functions remain the same...
const getLocalDateString = (date) => {
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate.toISOString().split('T')[0];
};

const getLocalSunday = (date) => {
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    const day = localDate.getDay();
    const diff = localDate.getDate() - day;
    return new Date(localDate.setDate(diff));
};

const utcToLocal = (utcDateString) => {
    const date = new Date(utcDateString);
    return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
};

const getWeekNumber = (date) => {
    // Use the same week number calculation as Timecard.js
    return format(date, 'w');
};

const WeeklyView = ({ onBack }) => {
    const [dataLoading, setDataLoading] = useState(false);
    const [error, setError] = useState(null);
    const [timecards, setTimecards] = useState([]);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [users, setUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const { user, loading: authLoading } = useAuth();

    // Memoize currentSunday to prevent unnecessary recalculations
    const currentSunday = useMemo(() => getLocalSunday(currentDate), [currentDate]);
    
    // Memoize week number
    const weekNumber = useMemo(() => getWeekNumber(currentSunday), [currentSunday]);
    
    // Check if user can view other users' timecards
    const canViewOtherUsers = user?.role === 'admin' || user?.role === 'team_lead' || user?.role === 'accountant';

    // Initialize component with user data and fetch timecards
    useEffect(() => {
        let mounted = true;

        const initializeComponent = async () => {
            if (!user) return;

            console.log('Initializing component with user:', {
                id: user.id,
                role: user.role,
                canViewOthers: canViewOtherUsers
            });

            try {
                setDataLoading(true);

                // Set selectedUserId to current user
                if (mounted) {
                    setSelectedUserId(user.id);
                }

                // Fetch users list if user can view others
                if (canViewOtherUsers) {
                    const data = await getAllUsersNoPagination();
                    if (mounted) {
                        // Filter out admin users but keep team leads and accountant users in reporting views
                        let filteredUsers = data.filter(u => u.role !== 'admin');
                        
                        // Apply role-based filtering
                        if (user.role === 'team_lead') {
                            console.log('User is a team lead');
                            
                            // Check if user has teamMembers directly
                            if (user.teamMembers && user.teamMembers.length > 0) {
                                console.log('Using teamMembers array directly:', user.teamMembers);
                                
                                // Filter users to only show the team lead and their team members
                                filteredUsers = filteredUsers.filter(u => 
                                    String(u._id) === String(user.id) || // Self
                                    user.teamMembers.some(id => String(id) === String(u._id)) // Team members
                                );
                            } else if (user.teams && user.teams.length > 0) {
                                // Get only the teams where the user is the team lead
                                const leadTeams = user.teams.filter(team => 
                                    String(team.teamLead) === String(user.id)
                                );
                                
                                if (leadTeams.length > 0) {
                                    // Get all members from these teams
                                    const teamMembers = leadTeams.flatMap(team => team.members || []);
                                    
                                    // Add the team lead to the list of members if not already included
                                    if (!teamMembers.includes(user.id) && !teamMembers.some(id => String(id) === String(user.id))) {
                                        teamMembers.push(user.id);
                                    }
                                    
                                    // Make sure we don't have duplicates
                                    const uniqueTeamMembers = [...new Set(teamMembers.map(id => String(id)))];
                                    
                                    // Filter users to only show the team lead and their team members
                                    filteredUsers = filteredUsers.filter(u => 
                                        uniqueTeamMembers.includes(String(u._id))
                                    );
                                } else {
                                    // If user is not a team lead for any team, they can only see themselves
                                    filteredUsers = filteredUsers.filter(u => String(u._id) === String(user.id));
                                }
                            } else {
                                // If user has no teams or teamMembers, they can only see themselves
                                filteredUsers = filteredUsers.filter(u => String(u._id) === String(user.id));
                            }
                        }
                        // Admins and accountants can see all non-admin users (already filtered above)
                        
                        // Sort users by name
                        const sortedUsers = filteredUsers.sort((a, b) => {
                            const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
                            const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
                            return nameA.localeCompare(nameB);
                        });
                        console.log('Users fetched:', {
                            count: sortedUsers.length,
                            currentUser: user.id
                        });
                        setUsers(sortedUsers);
                    }
                }

                // Fetch initial timecard data
                const dateString = getLocalDateString(currentSunday);
                console.log('Fetching initial timecards:', {
                    dateString,
                    userId: user.id,
                    weekNumber
                });
                
                const timecardData = await getWeekTimecards(dateString, user.id);
                if (mounted) {
                    console.log('Initial timecards received:', {
                        count: timecardData.length,
                        weekNumber,
                        timecards: timecardData.map(tc => ({
                            id: tc._id,
                            user: tc.user?.username || 'Unknown',
                            project: tc.project?.name || 'Unknown',
                            startDate: tc.startDate,
                            endDate: tc.endDate
                        }))
                    });
                    
                    // Additional filtering to ensure timecards are only shown in the correct week
                    const weekFilteredTimecards = timecardData.filter(timecard => {
                        // Calculate the week number for this timecard's start date
                        const timecardStartDate = new Date(timecard.startDate);
                        const timecardWeekInfo = getWeekInfo(timecardStartDate);
                        
                        // Instead of comparing week numbers (which can be inconsistent),
                        // check if the timecard's date range overlaps with the current week's date range
                        
                        // Get the current week's date range
                        const currentWeekStart = new Date(currentSunday);
                        const currentWeekEnd = new Date(currentSunday);
                        currentWeekEnd.setDate(currentWeekEnd.getDate() + 6);
                        
                        // Get the timecard's start and end dates
                        const tcStartDate = new Date(timecard.startDate);
                        const tcEndDate = new Date(timecard.endDate);
                        
                        // A timecard belongs to this week if its date range overlaps with the current week's date range
                        // This is true if:
                        // 1. The timecard's start date is within the current week, OR
                        // 2. The timecard's end date is within the current week, OR
                        // 3. The timecard spans the entire current week
                        const isCorrectWeek = 
                            // Case 1: Timecard starts within the week
                            (tcStartDate >= currentWeekStart && tcStartDate <= currentWeekEnd) ||
                            // Case 2: Timecard ends within the week
                            (tcEndDate >= currentWeekStart && tcEndDate <= currentWeekEnd) ||
                            // Case 3: Timecard spans the entire week
                            (tcStartDate <= currentWeekStart && tcEndDate >= currentWeekEnd);
                        
                        console.log('Frontend timecard week check (initial load):', {
                            id: timecard._id,
                            user: timecard.user?.username || 'Unknown',
                            timecardWeekNumber: timecardWeekInfo.weekNumber,
                            timecardYear: timecardWeekInfo.year,
                            currentWeekNumber: weekNumber,
                            isCorrectWeek
                        });
                        
                        return isCorrectWeek;
                    });
                    
                    console.log('Week timecards (after initial filtering):', {
                        beforeCount: timecardData.length,
                        afterCount: weekFilteredTimecards.length,
                        weekNumber
                    });
                    
                    setTimecards(weekFilteredTimecards);
                    setError(null);
                }
            } catch (err) {
                console.error('Error in initialization:', err);
                if (mounted) {
                    setError(err.message);
                }
            } finally {
                if (mounted) {
                    setDataLoading(false);
                }
            }
        };

        initializeComponent();

        return () => {
            mounted = false;
        };
    }, [user, canViewOtherUsers, currentSunday, weekNumber]);

    // Fetch timecards when selected user changes (after initial load)
    useEffect(() => {
        let mounted = true;
        const controller = new AbortController();

        const fetchTimecardData = async () => {
            // Skip if this is the initial load (handled by first useEffect)
            if (!selectedUserId || selectedUserId === user?.id) return;

            try {
                setDataLoading(true);
                const dateString = getLocalDateString(currentSunday);
                
                console.log('Fetching timecards for user change:', {
                    dateString,
                    userId: selectedUserId,
                    weekNumber,
                    userRole: user?.role
                });
                
                const data = await getWeekTimecards(dateString, selectedUserId);
                if (mounted) {
                    console.log('Timecards received:', {
                        count: data.length,
                        weekNumber,
                        timecards: data.map(tc => ({
                            id: tc._id,
                            user: tc.user?.username || 'Unknown',
                            project: tc.project?.name || 'Unknown',
                            startDate: tc.startDate,
                            endDate: tc.endDate
                        }))
                    });
                    
                    // Additional filtering to ensure timecards are only shown in the correct week
                    const weekFilteredTimecards = data.filter(timecard => {
                        // Calculate the week number for this timecard's start date
                        const timecardStartDate = new Date(timecard.startDate);
                        const timecardWeekInfo = getWeekInfo(timecardStartDate);
                        
                        // Instead of comparing week numbers (which can be inconsistent),
                        // check if the timecard's date range overlaps with the current week's date range
                        
                        // Get the current week's date range
                        const currentWeekStart = new Date(currentSunday);
                        const currentWeekEnd = new Date(currentSunday);
                        currentWeekEnd.setDate(currentWeekEnd.getDate() + 6);
                        
                        // Get the timecard's start and end dates
                        const tcStartDate = new Date(timecard.startDate);
                        const tcEndDate = new Date(timecard.endDate);
                        
                        // A timecard belongs to this week if its date range overlaps with the current week's date range
                        // This is true if:
                        // 1. The timecard's start date is within the current week, OR
                        // 2. The timecard's end date is within the current week, OR
                        // 3. The timecard spans the entire current week
                        const isCorrectWeek = 
                            // Case 1: Timecard starts within the week
                            (tcStartDate >= currentWeekStart && tcStartDate <= currentWeekEnd) ||
                            // Case 2: Timecard ends within the week
                            (tcEndDate >= currentWeekStart && tcEndDate <= currentWeekEnd) ||
                            // Case 3: Timecard spans the entire week
                            (tcStartDate <= currentWeekStart && tcEndDate >= currentWeekEnd);
                        
                        console.log('Frontend timecard week check (WeeklyView):', {
                            id: timecard._id,
                            user: timecard.user?.username || 'Unknown',
                            timecardWeekNumber: timecardWeekInfo.weekNumber,
                            timecardYear: timecardWeekInfo.year,
                            currentWeekNumber: weekNumber,
                            isCorrectWeek
                        });
                        
                        return isCorrectWeek;
                    });
                    
                    console.log('Week timecards (after frontend filtering):', {
                        beforeCount: data.length,
                        afterCount: weekFilteredTimecards.length,
                        weekNumber
                    });
                    
                    setTimecards(weekFilteredTimecards);
                    setError(null);
                }
            } catch (err) {
                if (err.name === 'AbortError') {
                    console.log('Fetch aborted');
                    return;
                }
                console.error('Error fetching timecards:', err);
                if (mounted) {
                    setError(err.message);
                    setTimecards([]);
                }
            } finally {
                if (mounted) {
                    setDataLoading(false);
                }
            }
        };

        fetchTimecardData();

        return () => {
            mounted = false;
            controller.abort();
        };
    }, [selectedUserId, currentSunday, weekNumber, user]);

    const handleUserChange = (event) => {
        const newUserId = event.target.value;
        console.log('User selection changed:', {
            newUserId,
            currentWeek: weekNumber
        });
        setSelectedUserId(newUserId);
    };

    const handlePreviousWeek = () => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            newDate.setDate(newDate.getDate() - 7);
            return newDate;
        });
    };

    const handleNextWeek = () => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            newDate.setDate(newDate.getDate() + 7);
            return newDate;
        });
    };


    // Memoize week info calculation
    const weekInfo = useMemo(() => {
        // Always base the week on currentDate, not timecard data
        const days = Array.from({ length: 7 }, (_, i) => {
            const date = new Date(currentSunday);
            date.setDate(currentSunday.getDate() + i);
            
            // If we have timecard data, use its hours
            const timecard = timecards[0];
            const timecardDay = timecard?.days[i];
            const hours = timecardDay ? parseFloat(timecardDay.hours) : 0;
            
            return {
                date: date.toISOString(),
                hours
            };
        });
        
        return { days };
    }, [currentSunday, timecards]);

    const { days } = weekInfo;
    const selectedUser = users.find(u => u._id === selectedUserId) || user;

    // Show loading state while auth is loading
    if (authLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    // Show error if no user data
    if (!user) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <Alert severity="error">Authentication required</Alert>
            </Box>
        );
    }

    console.log('Rendering week:', {
        weekNumber,
        currentSunday: currentSunday.toISOString(),
        selectedUser: selectedUser?.firstName,
        userRole: user.role
    });

    return (
        <Box sx={{ 
            p: 3,
            '@media (max-width:600px)': {
                p: 1, // Much more reduced padding for mobile
                paddingTop: 0, // No top padding - layout provides spacing
                paddingBottom: '88px', // Account for bottom navigation
            },
            '@media (max-width:900px) and (orientation: landscape)': {
                p: 0.5, // Minimal padding in landscape mode
                paddingTop: 0, // No top padding
                paddingBottom: 0.5, // Minimal bottom padding
                height: '100vh', // Force full viewport height
                overflow: 'visible', // Allow content to overflow
                minHeight: '100vh', // Ensure minimum full height
            }
        }}>
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Header */}
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 3,
                '@media (max-width:600px)': {
                    mb: 1, // Much more reduced margin for mobile
                },
                '@media (max-width:900px) and (orientation: landscape)': {
                    mb: 0.5, // Reduced margin in landscape mode but keep header visible
                }
            }}>
                <IconButton onClick={onBack} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6">
                    Weekly View - {selectedUser?.firstName} {selectedUser?.lastName}
                </Typography>
            </Box>

            {/* Navigation Bar */}
            <Paper sx={{ 
                p: 2, 
                mb: 3,
                '@media (max-width:600px)': {
                    p: 0, // Remove all padding on mobile
                    mb: 1.5,
                    // Add bottom padding when user dropdown is present
                    ...(canViewOtherUsers && users.length > 0 && {
                        pb: 2, // Add bottom padding to the Paper itself
                    })
                },
                '@media (max-width:900px) and (orientation: landscape)': {
                    overflow: 'visible', // Allow label to extend outside Paper boundaries
                    // Add top padding when user dropdown is present
                    ...(canViewOtherUsers && users.length > 0 && {
                        pt: 3, // Add top padding to the Paper itself in landscape
                    })
                }
            }}>
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    '@media (max-width:600px)': {
                        flexDirection: 'row', // Keep on same line for mobile
                        gap: 0.5,
                        alignItems: 'center',
                        px: 0.5, // Add minimal padding inside the flex container
                        py: 1,
                    }
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 2,
                        '@media (max-width:600px)': {
                            flex: 1,
                            gap: 0.5,
                            minWidth: 0, // Allow shrinking
                        }
                    }}>
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            '@media (max-width:600px)': {
                                flex: 1,
                                gap: 0.5,
                                marginLeft: '-8px', // Move the entire week navigation widget to the left
                            }
                        }}>
                            <IconButton 
                                onClick={handlePreviousWeek}
                                sx={{
                                    '@media (max-width:600px)': {
                                        padding: '4px',
                                    }
                                }}
                            >
                                <NavigateBeforeIcon />
                            </IconButton>
                            <Typography sx={{
                                '@media (max-width:600px)': {
                                    fontSize: '0.75rem',
                                    textAlign: 'center',
                                    flex: 1,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                },
                                '@media (max-width:900px) and (orientation: landscape)': {
                                    fontSize: '0.7rem', // Even smaller font in landscape
                                    textAlign: 'center',
                                    flex: 1,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    lineHeight: 1.2, // Tighter line height
                                }
                            }}>
                                Week {weekNumber}: {format(currentSunday, 'MMM d', { locale: enUS })} - {format(new Date(days[days.length - 1].date), 'MMM d, yyyy', { locale: enUS })}
                            </Typography>
                            <IconButton 
                                onClick={handleNextWeek}
                                sx={{
                                    '@media (max-width:600px)': {
                                        padding: '4px',
                                    }
                                }}
                            >
                                <NavigateNextIcon />
                            </IconButton>
                        </Box>
                        {canViewOtherUsers && users.length > 0 && (
                            <>
                                <Divider 
                                    orientation="vertical" 
                                    flexItem 
                                    sx={{
                                        '@media (max-width:600px)': {
                                            display: 'none',
                                        }
                                    }}
                                />
                                <FormControl 
                                    size="small" 
                                    sx={{ 
                                        minWidth: 200,
                                        '@media (max-width:600px)': {
                                            display: 'none', // Hide user selector on mobile portrait only
                                        }
                                    }}
                                >
                                    <InputLabel>User</InputLabel>
                                    <Select
                                        value={selectedUserId || ''}
                                        onChange={handleUserChange}
                                        label="User"
                                    >
                                        {users.map((u) => (
                                            <MenuItem key={u._id} value={u._id}>
                                                {u.firstName} {u.lastName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </>
                        )}
                    </Box>
                    <ExportButton
                        viewType="weekly"
                        timecards={timecards}
                        viewMode="singleUser"
                        isAllUsers={false}
                        user={selectedUser}
                        mobileIconOnly={true}
                    />
                </Box>
                {/* User selector on separate line for mobile portrait only */}
                {canViewOtherUsers && users.length > 0 && (
                    <Box sx={{
                        display: 'none',
                        '@media (max-width:600px) and (orientation: portrait)': {
                            display: 'block',
                            px: 1,
                            pt: 0.5,
                            mb: 0.5,
                            minHeight: '60px',
                        }
                    }}>
                        <FormControl 
                            size="small" 
                            sx={{ 
                                width: 'calc(100% - 16px)',
                                '@media (max-width:600px)': {
                                    mx: 1,
                                }
                            }}
                        >
                            <InputLabel>User</InputLabel>
                            <Select
                                value={selectedUserId || ''}
                                onChange={handleUserChange}
                                label="User"
                            >
                                {users.map((u) => (
                                    <MenuItem key={u._id} value={u._id}>
                                        {u.firstName} {u.lastName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                )}
            </Paper>

            {/* Timecard Table */}
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead sx={{ bgcolor: '#e0e0e0' }}>
                        <TableRow>
                            <TableCell sx={{ width: '9%' }}>Username</TableCell>
                            <TableCell sx={{ width: '12%' }}>Project</TableCell>
                            <TableCell sx={{ 
                                width: '12%', 
                                whiteSpace: 'nowrap',
                                '@media (max-width:900px)': {
                                    width: '25%', // Even more increased width for mobile
                                    minWidth: '100px', // Larger minimum width
                                }
                            }}>Date Range</TableCell>
                            {days.map((day, index) => {
                                const date = new Date(day.date);
                                return (
                                    <TableCell 
                                        key={index} 
                                        align="center" 
                                        sx={{ width: '10%' }}
                                    >
                                        {format(date, 'EEE', { locale: enUS })}<br />
                                        {format(date, 'MMM d', { locale: enUS })}
                                    </TableCell>
                                );
                            })}
                            <TableCell align="center">Total</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {timecards.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={11} align="center">
                                    No timecard data found for this week
                                </TableCell>
                            </TableRow>
                        ) : (
                            timecards.map((timecard) => (
                                <React.Fragment key={timecard._id}>
                                    <TableRow>
                                        <TableCell>{timecard.user.username}</TableCell>
                                        <TableCell>{timecard.project.name}</TableCell>
                                        <TableCell sx={{
                                            whiteSpace: 'nowrap',
                                            '@media (max-width:900px)': {
                                                fontSize: '0.75rem',
                                                whiteSpace: 'nowrap',
                                            }
                                        }}>
                                            <Box sx={{
                                                display: 'none',
                                                '@media (max-width:900px)': {
                                                    display: 'block',
                                                }
                                            }}>
                                                {format(utcToLocal(timecard.days[0].date), 'MM/dd', { locale: enUS })} - {format(utcToLocal(timecard.days[timecard.days.length - 1].date), 'MM/dd', { locale: enUS })}
                                            </Box>
                                            <Box sx={{
                                                display: 'block',
                                                '@media (max-width:900px)': {
                                                    display: 'none',
                                                }
                                            }}>
                                                {format(utcToLocal(timecard.days[0].date), 'MMM d', { locale: enUS })} - {format(utcToLocal(timecard.days[timecard.days.length - 1].date), 'MMM d', { locale: enUS })}
                                            </Box>
                                        </TableCell>
                                        {timecard.days.map((day, index) => (
                                            <TableCell key={index} align="center">
                                                {parseFloat(day.hours).toFixed(2)}
                                            </TableCell>
                                        ))}
                                        <TableCell align="center">
                                            {timecard.days.reduce((sum, day) => sum + parseFloat(day.hours), 0).toFixed(2)}
                                        </TableCell>
                                    </TableRow>
                                    {/* Comments row */}
                                    {timecard.days.some(day => day.comments) && (
                                        <TableRow>
                                            <TableCell />
                                            <TableCell />
                                            <TableCell />
                                            {timecard.days.map((day, index) => (
                                                <TableCell 
                                                    key={index} 
                                                    align="center"
                                                    sx={{ 
                                                        whiteSpace: 'pre-wrap',
                                                        fontSize: '0.875rem',
                                                        color: 'text.secondary'
                                                    }}
                                                >
                                                    {day.comments || ''}
                                                </TableCell>
                                            ))}
                                            <TableCell />
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            ))
                        )}
                        {/* Total row */}
                        {timecards.length > 0 && (
                            <TableRow sx={{ bgcolor: '#f8f8d7' }}>
                                <TableCell>Total</TableCell>
                                <TableCell />
                                <TableCell />
                                {days.map((_, index) => {
                                    const dayTotal = timecards.reduce((sum, timecard) => {
                                        return sum + parseFloat(timecard.days[index].hours);
                                    }, 0);
                                    return (
                                        <TableCell key={index} align="center">
                                            {dayTotal.toFixed(2)}
                                        </TableCell>
                                    );
                                })}
                                <TableCell align="center">
                                    {timecards.reduce((sum, timecard) => {
                                        return sum + timecard.days.reduce((daySum, day) => 
                                            daySum + parseFloat(day.hours), 0);
                                    }, 0).toFixed(2)}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            
            {dataLoading && (
                <Box sx={{ 
                    position: 'absolute', 
                    top: 0, 
                    left: 0, 
                    right: 0, 
                    bottom: 0, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    zIndex: 1000
                }}>
                    <CircularProgress />
                </Box>
            )}
        </Box>
    );
};

export default WeeklyView;
